import React, { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Bot } from "lucide-react";
import { useI18n } from "@/i18n";
import { API, logError } from "@/lib/apiClient";

const SESSION_KEY = "alanya_ai_session";

export const AIAssistant = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);
  const sessionId = useRef(sessionStorage.getItem(SESSION_KEY) || `sess-${Math.random().toString(36).slice(2)}`);

  useEffect(() => { sessionStorage.setItem(SESSION_KEY, sessionId.current); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-ai-assistant", openHandler);
    return () => window.removeEventListener("open-ai-assistant", openHandler);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    const turnId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setMessages((m) => [...m, { id: `u-${turnId}`, role: "user", content: text }, { id: `a-${turnId}`, role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId.current }),
      });
      if (!res.ok || !res.body) throw new Error(`AI request failed: ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            }
          } catch (err) { logError("SSE parse error", err); }
        }
      }
    } catch (e) {
      logError("AI chat request failed", e);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: "Sorry, something went wrong. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const suggestions = ["Plan a perfect day in Alanya", "Best boat tour for families?", "What can I do on a budget?"];

  const renderMarkdown = (s) => {
    const esc = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const html = esc
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/(^|[^*])\*(?!\s)(.+?)\*/g, "$1<em>$2</em>")
      .replace(/`(.+?)`/g, "<code>$1</code>")
      .replace(/\n/g, "<br/>");
    return DOMPurify.sanitize(html, { ALLOWED_TAGS: ["strong", "em", "code", "br"] });
  };

  return (
    <>
      <button
        data-testid="ai-assistant-toggle"
        onClick={() => setOpen((o) => !o)}
        className="hidden md:flex fixed bottom-5 right-5 z-40 items-center gap-2 rounded-full bg-primary text-primary-foreground pl-3 pr-4 py-3 shadow-lg hover:scale-105 active:scale-95 transition-transform"
      >
        <Sparkles className="h-5 w-5" />
        <span className="text-sm font-semibold hidden sm:inline">AI Assistant</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-20 right-4 z-50 w-[calc(100vw-2rem)] sm:w-96 h-[520px] max-h-[75vh] rounded-3xl bg-white border border-black/10 shadow-2xl flex flex-col overflow-hidden"
            data-testid="ai-assistant-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
              <div className="flex items-center gap-2">
                <div className="grid place-items-center h-8 w-8 rounded-full bg-white/20"><Bot className="h-4 w-4" /></div>
                <div>
                  <p className="font-semibold text-sm leading-none font-display">Alanya Assistant</p>
                  <p className="text-[11px] opacity-80 mt-0.5">Powered by GPT</p>
                </div>
              </div>
              <button data-testid="ai-close" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <div className="rounded-2xl bg-secondary p-3 text-sm">{t("ai_greeting")}</div>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((s) => (
                      <button key={s} data-testid="ai-suggestion" onClick={() => setInput(s)}
                        className="rounded-full border border-black/10 px-3 py-1.5 text-xs hover:bg-secondary transition-colors">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" ? (
                    <div
                      className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm bg-secondary [&_strong]:font-semibold [&_code]:bg-black/10 [&_code]:rounded [&_code]:px-1"
                      dangerouslySetInnerHTML={{ __html: m.content ? renderMarkdown(m.content) : (streaming && i === messages.length - 1 ? "…" : "") }}
                    />
                  ) : (
                    <div className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap bg-primary text-primary-foreground">
                      {m.content}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-black/5 flex items-center gap-2">
              <input
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask anything about Alanya..."
                className="flex-1 rounded-full bg-secondary px-4 py-2.5 text-sm outline-none focus:ring-2 ring-primary/30"
              />
              <button data-testid="ai-send" onClick={send} disabled={streaming}
                className="grid place-items-center h-10 w-10 rounded-full bg-primary text-primary-foreground disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
